import { reactive } from 'vue'

import {
  MicrofrontendEvents,
  onDispatchToastNotification,
  publish,
} from '@sennder/senn-node-microfrontend-event-bus'
import {
  IFeatureFlags,
  IMicrofrontendData,
  ISennTmsSharedData,
  IUser,
  IUserRole,
  opsProfileMock,
  OpsProfile,
} from '@sennder/senn-node-microfrontend-interfaces'

import router from '@/router'
import { logger } from '@/services/logger/loggers'
import { logout } from '@/store/logoutActions'
import errorsHandler from '@/services/errors-handler'
import { analytics } from '@/services/analyticsProvider'
import { i18n, defaultLanguage } from '@/services/i18n'
import {
  TENANT,
  SHARED_DATA_TYPE,
  ORCAS_URL,
  OPERATIONS_URL,
  SHOW_LANGUAGE_PICKER,
} from '@/common/config'
import { userManagementService } from '@/services/userManagementService'
import { sendErrorInMonitor } from '@/services/monitor'
import { notify } from './notify'
import {
  getAuthToken,
  getAuthHeader,
  getCommonHeaders,
  getPermissions,
} from '@/services/authService'
import startAsyncService from '@/services/retry-service'
import { Prettify, Optional } from '@/types/types'
import { featureFlag } from '@/services/featureFlagProvider'
import {
  findCharteringTeam,
  sennteamsService,
  UserTeam,
} from '@/services/teamsService'

export type SennTmsStoreData = Prettify<
  Optional<ISennTmsSharedData<object>, 'profile'>
> & {
  user?: IUser
  roles: IUserRole[]
  teams?: UserTeam[]
}

const getEmptyData = (): SennTmsStoreData => {
  return {
    language: defaultLanguage,
    tenant: TENANT,
    type: SHARED_DATA_TYPE,
    featureFlags: {},
    /**
     * TODO: some Octopus micro-frontends expect motership and orcas urls to be passed by the shell because of the multi-tenancy.
     * We can get rid of this and define all the URLs in the micro-frontends themselves.
     */
    env: {
      OPERATIONS_BACKEND_URL: OPERATIONS_URL as string,
      ORCAS_URL: ORCAS_URL as string,
    },
    roles: [],
  }
}

export const store = reactive({
  state: {
    data: getEmptyData(),
    callbacks: {
      getToken: getAuthHeader,
      getAuthToken,
      getAuthHeader,
      getCommonHeaders,
      getPermissions,
      syncParentRouter: async (route: string | { path: string }) => {
        const currentPath = router.currentRoute.value.fullPath
        if (
          (typeof route === 'string' && currentPath === route) ||
          (typeof route === 'object' && currentPath === route.path)
        ) {
          return
        }
        await router.push(route)
      },
      segmentTrackEvent: () => {},
      onUnauthorized: logout,
      onUnhandledError: errorsHandler,
    },
    providers: {
      featureFlag: {
        getFeatureFlag: featureFlag.getFeatureFlag,
        watchFeatureFlag: featureFlag.watchFeatureFlag,
        trackEvent: featureFlag.trackEvent,
      },
      monitoring: {
        sendError: sendErrorInMonitor,
      },
      logger,
      notifications: {
        error: (message: string) => notify(message, 'error'),
        success: (message: string) => notify(message, 'success'),
      },
      analytics,
    },
  } satisfies IMicrofrontendData<SennTmsStoreData>,
})

export const getStateData = (): SennTmsStoreData => {
  return store.state.data
}

// Loosely typed and dangerous, only use this function from dev-panel
export const DEV_setStateData = (data: object) => {
  // @ts-expect-error
  store.state.data = data
}

export const getUserProfile = () => {
  if (!store.state.data.profile) {
    throw new Error(
      '[tms-shell - getUserProfile] state.data.profile is not initialized'
    )
  }

  return store.state.data.profile
}

export const getStateProviders = () => {
  return store.state.providers
}

export const getStateCallbacks = () => {
  return store.state.callbacks
}

export const loadState = async () => {
  let user: IUser
  let opsProfile: OpsProfile

  // 1. Fetch user data, set language
  try {
    ;({ opsProfile, user } = await userManagementService.getUserAndProfile())

    setStoreLanguage(SHOW_LANGUAGE_PICKER ? user.language : 'en')
  } catch (error) {
    logger.error(
      '[tms-shell - userManagementService]: User data can not be loaded',
      {
        error,
      }
    )

    publish<onDispatchToastNotification>({
      name: MicrofrontendEvents.onDispatchToastNotification,
      data: {
        message: i18n.global.t('toast.error.message'),
        category: 'error',
        title: i18n.global.t('toast.error.title'),
      },
    })
    await logout()
    return false
  }

  // 2. Fetch user teams, it is non-blocking - it's ok to proceed without teams
  await fetchTeams(user.email)

  // 3. Fetch user roles and identify user in LaunchDarkly
  const [userRoles] = await Promise.all([
    fetchUserRoles(opsProfile.id),
    startAsyncService(
      'LaunchDarkly',
      () =>
        featureFlag.identify(opsProfile.id, {
          email: opsProfile.email,
          name: opsProfile.name,
        }),
      {},
      0
    ),
  ])

  // 4. Update store with user, profile and roles
  store.state.data = {
    ...store.state.data,
    user,
    profile: opsProfile,
    roles: userRoles || [],
  }

  return true
}

const fetchUserRoles = async (id: string) => {
  if (!id) {
    return
  }

  try {
    const uMRoles = await userManagementService.getUserRoles(id)
    const userRoles = [...uMRoles]

    // Set Chartering Roles (if any present)
    const charteringTeam = findCharteringTeam(store.state.data.teams || [])

    if (charteringTeam) {
      /**
       * Legacy alert!
       * We populate two teams to have both team role and the name. Yes, this is weird ¯\_(ツ)_/¯
       * It keeps backward compatibility though since we're passing this data to the micro-frontends.
       * TODO: Remove this when the roles are removed from the shared data
       */

      if (charteringTeam) {
        const baseRole: IUserRole = {
          id: charteringTeam.id,
          name: charteringTeam.is_lead ? 'lead' : 'member',
          description: charteringTeam.name,
        }

        userRoles.push(baseRole)

        userRoles.push({
          ...baseRole,
          // @ts-expect-error team_name is defined since we filter out no-name teams in findCharteringTeam
          name: charteringTeam.team_name,
        })
      }
    }

    return userRoles
  } catch (error) {
    logger.error(
      '[tms-shell - userManagementService]: User roles can not be load',
      { error }
    )
  }
}

/**
 * Fetches and saves to the store user's teams.
 * @param email - user email
 */
const fetchTeams = async (email: string) => {
  try {
    store.state.data.teams = await sennteamsService.getUserTeams(email)
  } catch (error) {
    logger.error('[tms-shell - fetchTeams]: User teams can not be load', {
      error,
    })
  }
}

export const setStoreLanguage = (language: string) => {
  store.state.data.language = language
}

/**
 * Updates the shell's store with new feature flags.
 * When all micro-frontends are migrated to use the new feature flag provider, this function and the related legacy flags in the store can be removed. A new, not shared state can be introduced to store flags for the shell logic.
 * @deprecated Currently is used only to maintain backwards compatibility with the old feature flags setup. Prefer using the featureFlag provider for new implementations.
 */
export const setStoreLegacyFlags = (flags: IFeatureFlags) => {
  store.state.data.featureFlags = {
    ...store.state.data.featureFlags,
    ...flags,
  }
}

/**
 * Returns the feature flags from the shell's store.
 * These flags are intended to be used only by micro-frontends that haven't migrated yet to the new feature flag provider and still rely on the old setup where the shell passes the flags down through the shared state, or for the internal shell's usage of feature flags.
 * When all micro-frontends are migrated to use the new feature flag provider, this function and the related legacy flags in the store can be removed. A new, not shared state can be introduced to store flags for the shell logic.
 * @deprecated Currently is used only to maintain backwards compatibility with the old feature flags setup. Prefer using the featureFlag provider for new implementations.
 */
export const getStoreLegacyFlags = () => {
  return store.state.data.featureFlags
}

export const setStoreData = (data: SennTmsStoreData) => {
  store.state.data = data
}

export const clearState = () => {
  store.state.data = getEmptyData()
}

const isStoreReady = (
  data: ISennTmsSharedData | SennTmsStoreData
): data is ISennTmsSharedData => {
  if ('user' in data && data.profile !== opsProfileMock) {
    return true
  }

  return false
}

/**
 * Creates a reactive projection of the store's shared data for a given micro-frontend.
 * The projection includes only the fields that are relevant to the micro-frontend, and it ensures that the store is ready before returning the data.
 */
export const getMicrofrontendData = (npmName: string): ISennTmsSharedData => {
  const data = getStateData()

  /**
   * Throw if the store is not yet ready.
   * Only the internal-auth-mf-component-v2 micro-frontend is allowed to access the store before it is ready, as it is responsible for initializing the store.
   */
  if (!isStoreReady(data) && npmName !== 'internal-auth-mf-component-v2') {
    throw new Error(
      `state.data.profile is not initialized (Loading ${npmName})`
    )
  }

  const projection = reactive({
    get type() {
      return store.state.data.type
    },
    get env() {
      return store.state.data.env
    },
    get language() {
      return store.state.data.language
    },
    get tenant() {
      return store.state.data.tenant
    },
    get featureFlags() {
      return store.state.data.featureFlags
    },
    get profile() {
      return store.state.data.profile as OpsProfile
    },
    get user() {
      return store.state.data.user
    },
    get roles() {
      return store.state.data.roles
    },
  })

  return projection
}
